import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

function AdminDashboard() {
  const [usersCount, setUsersCount] = useState(0);
  const [pendingKYC, setPendingKYC] = useState(0);
  const [openTickets, setOpenTickets] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const [activeCases, setActiveCases] = useState(0);
  const [pendingReleases, setPendingReleases] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);

  useEffect(() => {
    // Fetch stats from the same endpoints you already use
    api.get('/api/admin/users')
      .then(res => {
        setUsersCount(res.data.length);
        const total = res.data.reduce((sum, u) => sum + (u.balance || 0), 0);
        setTotalBalance(total);
      })
      .catch(err => console.error(err));

    api.get('/api/admin/kyc')
      .then(res => setPendingKYC(res.data.filter(k => k.status === 'pending').length))
      .catch(err => console.error(err));

    api.get('/api/admin/tickets')
      .then(res => setOpenTickets(res.data.filter(t => t.status === 'open').length))
      .catch(err => console.error(err));

    api.get('/api/admin/withdrawals')
      .then(res => setPendingWithdrawals(res.data.filter(w => w.status === 'pending').length))
      .catch(err => console.error(err));

    // Fetch releases for active cases & pending releases
    api.get('/api/releases')
      .then(res => {
        const releases = res.data || [];
        const active = releases.filter(r => r.status !== 'final_approved' && r.status !== 'rejected').length;
        setActiveCases(active);
        const pending = releases.filter(r => r.status === 'pending_risk').length;
        setPendingReleases(pending);
      })
      .catch(err => console.error(err));
  }, []);

  const cards = [
    {
      title: 'User Management',
      description: 'View all users, fund accounts, send custom emails, set withdrawal messages.',
      icon: '👥',
      link: '/admin/users',
      stat: `${usersCount} users`,
    },
    {
      title: 'Release Management',
      description: 'Review and approve releases (Risk → Compliance → Management → Final).',
      icon: '🔓',
      link: '/releases',
      stat: `${pendingReleases} pending risk`,
    },
    {
      title: 'Active Cases',
      description: 'All pending release requests that require attention.',
      icon: '⚖️',
      link: '/cases',
      stat: `${activeCases} active`,
    },
    {
      title: 'KYC Verification',
      description: 'Review and approve or reject identity submissions.',
      icon: '🆔',
      link: '/admin/kyc',
      stat: `${pendingKYC} pending`,
    },
    {
      title: 'Support Tickets',
      description: 'Respond to user support requests.',
      icon: '🎫',
      link: '/admin/tickets',
      stat: `${openTickets} open`,
    },
    {
      title: 'Audit Logs',
      description: 'Full trail of all system actions.',
      icon: '📋',
      link: '/admin/logs',
    },
    {
      title: 'Withdrawal Requests',
      description: 'Review, set network fees, and approve/reject crypto withdrawals.',
      icon: '💸',
      link: '/admin/withdrawals',
      stat: `${pendingWithdrawals} pending`,
    },
    {
      title: 'Reports',
      description: 'Overview of all vault activities and transaction summaries.',
      icon: '📄',
      link: '/reports',
    },
  ];

  return (
    <div>
      <h2>Administrator Dashboard</h2>
      <p style={{ color: '#64748b', marginBottom: '2rem' }}>
        Welcome to the Recovery Vault control centre.
      </p>

      {/* Top stat cards – reuse the same card styles */}
      <div className="stat-cards" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="card">
          <h3>Total Users</h3>
          <p>{usersCount}</p>
        </div>
        <div className="card">
          <h3>Active Cases</h3>
          <p>{activeCases}</p>
        </div>
        <div className="card">
          <h3>Pending KYC</h3>
          <p>{pendingKYC}</p>
        </div>
        <div className="card">
          <h3>Open Tickets</h3>
          <p>{openTickets}</p>
        </div>
      </div>

      {/* Main function cards – grid of clickable boxes */}
      <div className="service-grid" style={{ marginTop: '2rem' }}>
        {cards.map(card => (
          <Link
            to={card.link}
            key={card.title}
            className="service-card"
            style={{ textAlign: 'left', padding: '1.5rem', color: 'inherit' }}
          >
            <span className="service-icon" style={{ fontSize: '2rem' }}>{card.icon}</span>
            <h3>{card.title}</h3>
            <p style={{ color: '#475569', marginBottom: '0.5rem' }}>{card.description}</p>
            {card.stat && (
              <span style={{ fontWeight: 600, color: '#3b82f6' }}>{card.stat}</span>
            )}
          </Link>
        ))}
      </div>

      {/* Quick note – total vault balance */}
      <div style={{ marginTop: '2rem', color: '#475569' }}>
        <strong>Total vault balance (all users):</strong> {totalBalance.toFixed(2)} (approx)
      </div>
    </div>
  );
}

export default AdminDashboard;
